/**
 * Constraint radar — visual risk profile from interpretation categories only.
 */
import { Box, Stack, Typography } from "@mui/material";
import type { SoilInterpretationDTO } from "@flaha/shared-types";

import { GlassSurface } from "../../../layouts/components/GlassSurface";
import { chartTheme } from "../../soil/charts/utils/chartTheme";
import {
	buildConstraintRadarAxes,
	type RadarAxis,
} from "../utils/constraintRadar";

const SIZE = 240;
const CX = SIZE / 2;
const CY = SIZE / 2;
const R = 88;

interface ConstraintRadarProps {
	interpretation: SoilInterpretationDTO | null | undefined;
}

function polar(index: number, total: number, radius: number) {
	const angle = (-Math.PI / 2) + (index / total) * Math.PI * 2;
	return {
		x: CX + radius * Math.cos(angle),
		y: CY + radius * Math.sin(angle),
	};
}

function polygonPoints(axes: RadarAxis[], scale: (risk: number) => number) {
	return axes
		.map((axis, i) => {
			const p = polar(i, axes.length, scale(axis.risk));
			return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
		})
		.join(" ");
}

function pointColor(tone: RadarAxis["tone"]): string {
	if (tone === "poor") return chartTheme.radarPointPoor;
	if (tone === "fair") return chartTheme.radarPointFair;
	if (tone === "good") return chartTheme.radarPointGood;
	return chartTheme.labelMuted;
}

export function ConstraintRadar({ interpretation }: ConstraintRadarProps) {
	const axes = buildConstraintRadarAxes(interpretation);
	const rings = [0.25, 0.5, 0.75, 1];

	return (
		<GlassSurface
			component="section"
			aria-labelledby="constraint-radar-title"
			intensity="standard"
			depth="raised"
			sx={{ p: { xs: 2, md: 2.5 }, height: "100%" }}
			data-testid="constraint-radar"
		>
			<Stack spacing={1.5} alignItems="center">
				<Box sx={{ width: "100%" }}>
					<Typography
						id="constraint-radar-title"
						component="h2"
						variant="overline"
						sx={{ color: "workspace.blue", letterSpacing: "0.1em", fontWeight: 700 }}
					>
						Constraint radar
					</Typography>
					<Typography variant="body2" sx={{ color: "workspace.textMuted", mt: 0.5 }}>
						Outer edge = higher management pressure. Built only from
						interpretation categories already on this test.
					</Typography>
				</Box>

				<Box
					component="svg"
					viewBox={`0 0 ${SIZE} ${SIZE}`}
					role="img"
					aria-label="Constraint radar chart"
					sx={{ width: "100%", maxWidth: SIZE, height: "auto" }}
				>
					{rings.map((frac) => (
						<circle
							key={frac}
							cx={CX}
							cy={CY}
							r={R * frac}
							fill="none"
							stroke={chartTheme.radarGrid}
							strokeWidth={1}
						/>
					))}
					{axes.map((axis, i) => {
						const tip = polar(i, axes.length, R);
						const labelPos = polar(i, axes.length, R + 22);
						return (
							<g key={axis.key}>
								<line
									x1={CX}
									y1={CY}
									x2={tip.x}
									y2={tip.y}
									stroke={chartTheme.radarGrid}
									strokeWidth={1}
								/>
								<text
									x={labelPos.x}
									y={labelPos.y}
									textAnchor="middle"
									dominantBaseline="middle"
									fontSize={11}
									fill={chartTheme.label}
									fontWeight={600}
								>
									{axis.label}
								</text>
							</g>
						);
					})}
					<polygon
						points={polygonPoints(axes, (risk) => R * risk)}
						fill={chartTheme.radarFill}
						stroke={chartTheme.radarStroke}
						strokeWidth={2}
						data-testid="constraint-radar-polygon"
					/>
					{axes.map((axis, i) => {
						const p = polar(i, axes.length, R * axis.risk);
						return (
							<circle
								key={`pt-${axis.key}`}
								cx={p.x}
								cy={p.y}
								r={4.5}
								fill={pointColor(axis.tone)}
								stroke={chartTheme.pointStroke}
								strokeWidth={1.5}
							>
								<title>
									{axis.label}: {axis.value ?? "—"}
								</title>
							</circle>
						);
					})}
				</Box>

				{!interpretation ? (
					<Typography variant="caption" sx={{ color: "workspace.textMuted" }}>
						Interpretation unavailable — radar shows baseline only.
					</Typography>
				) : null}
			</Stack>
		</GlassSurface>
	);
}
